import { useState, useEffect, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { apiFetch } from "./api";
import { AppContext } from "./AppContext";
import AppShell from "./layout/AppShell";
import DashboardPage from "./pages/DashboardPage";
import ProductsPage from "./pages/ProductsPage";
import HistoryPage from "./pages/HistoryPage";
import SettingsPage from "./pages/SettingsPage";
import LoginScreen from "./screens/LoginScreen";

// ── Main App — router + auth gate + globální stav ──────────────────────────────
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("rk_token") || null);
  const [license, setLicense] = useState(() => { try { return JSON.parse(localStorage.getItem("rk_license") || "null"); } catch(e) { return null; } });
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [statFilter, setStatFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showTour, setShowTour] = useState(false);
  const [error, setError] = useState("");
  const [licenseSynced, setLicenseSynced] = useState(false);
  const [redirectToSettings, setRedirectToSettings] = useState(false);
  // Ceník i názvy plánů se tahají jednou a sdílí, ať je nemá frontend
  // zkopírované u sebe a nemůžou se rozejít s backendem.
  const [cenik, setCenik] = useState([]);
  const [frekvence, setFrekvence] = useState([]);
  const [nazvyPlanu, setNazvyPlanu] = useState({});

  useEffect(() => {
    let zivy = true;
    apiFetch("/plans", null)
      .then(d => {
        if (!zivy) return;
        setCenik(d.plans || []);
        setFrekvence(d.frequency_addons || []);
        setNazvyPlanu(d.names || {});
      })
      .catch(() => {});
    return () => { zivy = false; };
  }, []);

  const loadProducts = useCallback(async () => {
    if (!token) return;
    setLoadingProducts(true);
    try {
      const data = await apiFetch("/products", token);
      setProducts(data);
    } catch (e) {
      setError("Nepodařilo se načíst produkty: " + e.message);
    }
    setLoadingProducts(false);
  }, [token]);

  useEffect(() => { loadProducts(); }, [loadProducts]);


  // Refresh licence pri startu
  useEffect(() => {
    if (!token) return;
    apiFetch("/me", token).then(data => {
      setLicense(data);
      localStorage.setItem("rk_license", JSON.stringify(data));
      setLicenseSynced(true);
      if (!data.is_active || data.days_remaining <= 0) {
        setRedirectToSettings(true);
      }
    }).catch(() => {});
  }, [token]);

  // Refresh licence po návratu ze Stripe (addon_success nebo checkout_success)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("addon_success") || params.get("checkout_success")) {
      apiFetch("/me", token).then(data => {
        setLicense(data);
        localStorage.setItem("rk_license", JSON.stringify(data));
        window.history.replaceState({}, "", window.location.pathname);
      }).catch(() => {});
    }
  }, [token]);

  // ── průvodci: nikdy naráz, vždy po sobě ────────────────────────────────────
  // Bez shop_id neumí scheduler poznat, které nabídky jsou uživatelovy, takže
  // je onboarding předpoklad, ne soused prohlídky. Obojí čeká na /me, aby se
  // nerozhodovalo podle zastaralé licence z localStorage.
  const shopMissing = !!(licenseSynced && license && !license.shop_id);
  const needsOnboarding = !!(shopMissing && !license.onboarding_skipped_at);
  const tourPending = !!(licenseSynced && license && !license.tour_done_at && !needsOnboarding && !loadingProducts);

  // Prohlídka startuje s malou prodlevou, ať se stránka stihne vykreslit.
  useEffect(() => {
    if (!tourPending) return;
    const t = setTimeout(() => setShowTour(true), 600);
    return () => clearTimeout(t);
  }, [tourPending]);

  const loadGroups = useCallback(async () => {
    try { const data = await apiFetch("/groups", token); setGroups(data); } catch(e) {}
  }, [token]);
  useEffect(() => { if (token) loadGroups(); }, [token, loadGroups]);

  const handleLogin = (t, licenseData) => {
    setToken(t);
    setLicense(licenseData);
    localStorage.setItem("rk_token", t);
    localStorage.setItem("rk_license", JSON.stringify(licenseData));
    // Onboarding ani tour se nespouští tady — plynou ze stavu licence níž,
    // takže přežijí reload a fungují i u uživatele s uloženým tokenem.
    setLicenseSynced(true);
  };

  const logout = () => {
    localStorage.removeItem("rk_token");
    localStorage.removeItem("rk_license");
    setToken(null); setLicense(null); setProducts([]); setGroups([]); setLicenseSynced(false);
    setSelectedProduct(null); setSearchQuery(""); setStatFilter(null); setActiveGroup(null);
  };

  const saveLicense = (updated) => {
    setLicense(updated);
    localStorage.setItem("rk_license", JSON.stringify(updated));
  };

  // Příznaky průvodců žijí na licenci, aby se neopakovaly na každém zařízení.
  // Když PATCH selže, zapíšeme je aspoň lokálně — příští /me to srovná.
  const patchLicense = async (body, fallback) => {
    try {
      saveLicense(await apiFetch("/me/settings", token, { method: "PATCH", body: JSON.stringify(body) }));
    } catch (e) {
      saveLicense({ ...license, ...fallback });
    }
  };

  const finishTour = () => {
    setShowTour(false);
    patchLicense({ tour_done: true }, { tour_done_at: new Date().toISOString() });
  };

  const restartTour = () => patchLicense({ tour_done: false }, { tour_done_at: null });

  const completeOnboarding = (shopId) => {
    if (shopId) saveLicense({ ...license, shop_id: shopId });
    else patchLicense({ onboarding_skipped: true }, { onboarding_skipped_at: new Date().toISOString() });
    loadProducts();
  };

  const restartOnboarding = () => patchLicense({ onboarding_skipped: false }, { onboarding_skipped_at: null });

  // ── odvozená data (filtry + souhrnná čísla) ─────────────────────────────────
  const groupFiltered = useMemo(
    () => activeGroup === null ? products : products.filter(p => p.group_id === activeGroup),
    [products, activeGroup]
  );

  const filteredProducts = useMemo(() => {
    const byStat = statFilter === "top3" ? groupFiltered.filter(p => p.position && p.position <= 3)
      : statFilter === "improved" ? groupFiltered.filter(p => p.position && p.previous_position && p.position < p.previous_position)
      : statFilter === "declined" ? groupFiltered.filter(p => p.position && p.previous_position && p.position > p.previous_position)
      : statFilter === "first" ? groupFiltered.filter(p => p.position === 1)
      : groupFiltered;
    const q = searchQuery.trim().toLowerCase();
    return q ? byStat.filter(p => (p.name || "").toLowerCase().includes(q)) : byStat;
  }, [groupFiltered, statFilter, searchQuery]);

  // Souhrnná čísla (zdravotní karta) se počítají z celé skupiny, NE ze statistického filtru,
  // aby karta zůstala stabilní i když uživatel klikne na pilulku.
  const stats = useMemo(() => {
    const avgPos = groupFiltered.filter(p => p.position).length
      ? (groupFiltered.reduce((a, p) => a + (p.position || 0), 0) / groupFiltered.filter(p => p.position).length).toFixed(1)
      : "—";
    const withBoth = groupFiltered.filter(p => p.position && p.previous_position);
    const avgPosPrev = withBoth.length
      ? (withBoth.reduce((a, p) => a + p.previous_position, 0) / withBoth.length)
      : null;
    const avgPosNowForCmp = withBoth.length
      ? (withBoth.reduce((a, p) => a + p.position, 0) / withBoth.length)
      : null;
    const avgDelta = (avgPosPrev !== null && avgPosNowForCmp !== null)
      ? (avgPosPrev - avgPosNowForCmp) // kladné = zlepšení (nižší pozice je lepší)
      : null;
    return {
      avgPos,
      avgDelta,
      top3: groupFiltered.filter(p => p.position && p.position <= 3).length,
      improved: groupFiltered.filter(p => p.position && p.previous_position && p.position < p.previous_position).length,
      declined: groupFiltered.filter(p => p.position && p.previous_position && p.position > p.previous_position).length,
      firstPlace: groupFiltered.filter(p => p.position === 1).length,
      withPosition: groupFiltered.filter(p => p.position).length,
    };
  }, [groupFiltered]);

  if (!token) return <LoginScreen onLogin={handleLogin} />;

  const ctx = {
    token, license, setLicense, saveLicense, logout,
    products, setProducts, loadProducts, loadingProducts, error,
    groups, setGroups, loadGroups,
    activeGroup, setActiveGroup,
    statFilter, setStatFilter,
    searchQuery, setSearchQuery,
    groupFiltered, filteredProducts, stats,
    cenik, frekvence, nazvyPlanu,
    selectedProduct, setSelectedProduct,
    showAddModal, setShowAddModal,
    showGroupModal, setShowGroupModal,
    showTour, finishTour, restartTour,
    needsOnboarding, completeOnboarding, restartOnboarding,
    shopMissing,
    redirectToSettings, clearSettingsRedirect: () => setRedirectToSettings(false),
  };

  return (
    <AppContext.Provider value={ctx}>
      <BrowserRouter>
        <Routes>
          <Route element={<AppShell />}>
            <Route index element={<DashboardPage />} />
            <Route path="produkty" element={<ProductsPage />} />
            <Route path="historie" element={<HistoryPage />} />
            <Route path="nastaveni" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AppContext.Provider>
  );
}
